import React, { useCallback, useContext, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import useQueryParams from 'hooks/useQueryParams';
import { useTranslation } from 'react-i18next';
import { Field, Form } from 'react-final-form';
import useValidations from '../../hooks/useValidations';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import auth from 'api/auth';
import { Button } from 'ncoded-component-library';
import PasswordField from 'components/PasswordField';
import { FORGOT_PASSWORD_ROUTE, LOGIN_ROUTE } from 'data/global';

import './ResetPassword.styles.scss';

type ResetPasswordProps = {
  className?: string;
};

const ResetPassword: React.FC<ResetPasswordProps> = () => {
  const {
    params: { token },
  } = useQueryParams<{ token: string }>();

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const { t } = useTranslation();

  const { popServerError } = useContext(PopNotificationsContext);

  const validations = useValidations();

  const onSubmit = useCallback(
    async ({ newPassword }: { newPassword: string }) => {
      try {
        setLoading(true);
        await auth.resetPassword(token, newPassword);
        setSuccess(true);
      } catch (er) {
        popServerError(er);
      }

      setLoading(false);
    },
    [popServerError, token],
  );

  if (!token) return <Navigate to={FORGOT_PASSWORD_ROUTE} replace />;

  if (success) {
    return (
      <>
        <h1>{t('resetPasswordSuccess')}</h1>
        <p>
          {t('resetPasswordSuccessDescription')}{' '}
          <Link to={LOGIN_ROUTE}>{t('goTo', { page: t('signIn') })}</Link>
        </p>
      </>
    );
  }

  return (
    <>
      <h1>{t('resetPassword')}</h1>
      <Form
        onSubmit={onSubmit}
        render={({ handleSubmit }) => (
          <form className="auth-login" onSubmit={handleSubmit}>
            <Field
              placeholder={t('password')}
              label={t('password')}
              required
              name="newPassword"
              component={PasswordField}
              validate={validations.password}
            />
            <div className="actions">
              <Button type="submit" disabled={loading} data-loading={loading}>
                {t('resetPassword')}
              </Button>
            </div>
          </form>
        )}
      />
    </>
  );
};

export default ResetPassword;
